import { Button, DefaultRow, Inline, Table as TableCanoe, TableColumn } from 'canoe';
import {
  createCacheKey,
  ENDPOINTS,
  fetchResource,
  handleErrorResponse,
  useCreatePayload,
} from 'hammock';
import React, { useCallback, useMemo, useState } from 'react';
import { useInfiniteQuery } from 'react-query';

import { DehydratedAnalyticsQuery, SortOrder } from '@candu-ai/analytics/types';
import { hydrateAnalyticsData } from '@candu-ai/analytics/utils';

import { useDownloadAnalyticsQuery } from '../DownloadRecords/useDownloadAnalyticQuery';

export interface AnalyticsTableProps<QueryData = DefaultRow> {
  id: string;
  columns: TableColumn<QueryData>[];
  onSearchParamChange?: (args: { key: string; value: string | undefined }[]) => void;
  onRowClick?: (record: QueryData) => void;
  query?: Record<string, string | null>;
  pageCount?: number;
  emptyStateCanduSlug?: string;
  showDownloadInHeader?: boolean;
}

export const PAGE_COUNT = 20;
export const AnalyticsTable = <QueryData extends DefaultRow>({
  id,
  columns,
  onRowClick,
  onSearchParamChange,
  pageCount = PAGE_COUNT,
  query,
  emptyStateCanduSlug,
  showDownloadInHeader = true,
}: AnalyticsTableProps<QueryData>) => {
  const { qf, q, sf, so, ...otherQueryParams } = query || {};
  const [sortValue, setSortValue] = useState<{
    sortBy: keyof QueryData;
    sortOrder: SortOrder;
  } | null>(
    sf && so
      ? {
          sortBy: sf as keyof QueryData,
          sortOrder: so as SortOrder,
        }
      : null,
  );

  const [searchValue, setSearchValue] = useState<{
    searchBy: string;
    searchText: string;
  } | null>(
    qf && q
      ? {
          searchBy: qf as string,
          searchText: q as string,
        }
      : null,
  );

  const { download: downloadTable } = useDownloadAnalyticsQuery(query as any);

  const onDownload = async () => {
    const url = await downloadTable({ queryId: id });
    window.open(url);
  };

  const { get } = useCreatePayload(ENDPOINTS.analyticQueries);
  const payload = (pageParam = 0) =>
    get({
      id,
      query: {
        ...(searchValue
          ? {
              qf: searchValue.searchBy,
              q: searchValue.searchText,
            }
          : {}),
        ...(sortValue
          ? {
              so: sortValue.sortOrder,
              sf: sortValue.sortBy,
            }
          : {}),

        limit: pageCount + 1,
        offset: pageParam * pageCount,
        ...(otherQueryParams || {}),
      },
    });

  const {
    data,
    isLoading,
    isLoadingError,
    isFetchingNextPage,
    fetchNextPage,
    isRefetchError,
    isFetching,
    refetch,
    hasNextPage,
  } = useInfiniteQuery(
    [...createCacheKey(payload(0)), 'infinite-paginated'],
    async ({ pageParam = 0 }) => {
      const pagePayload = payload(pageParam);
      const responses = await fetchResource<DehydratedAnalyticsQuery>(pagePayload)
        .then((res) => hydrateAnalyticsData<QueryData>(res))
        .catch((e) => handleErrorResponse(e, pagePayload));

      if (responses && responses?.rows.length > pageCount) {
        responses.rows.pop();
        return { edges: responses, hasNextStep: true };
      }
      return { edges: responses, hasNextStep: false };
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.hasNextStep) {
          return allPages.length;
        }
        return undefined;
      },
      keepPreviousData: true,
      refetchInterval: 60 * 1000, // refetch every minute
    },
  );
  const firstPage = data?.pages[0].edges;
  const { sortOrder, sortField } = firstPage?.metadata || {};

  const columnsWithOptions: TableColumn<QueryData>[] = useMemo(() => {
    const responses = data?.pages[0].edges;

    if (!firstPage) {
      return columns;
    }
    return columns.map((column) => {
      const fieldOptions = responses?.fields.find(
        (field) => field.name === column.dataIndex || field.name === column.key,
      );
      return {
        ...column,
        isSearchable: (!!fieldOptions?.isSearchable || column.isSearchable) && !column.hideSearch,
        isSortable: !!fieldOptions?.isSortable || column.isSortable,
      };
    });
  }, [columns, firstPage]);

  const onRows = useCallback(
    (record) => ({
      onClick: () => onRowClick?.(record),
    }),
    [onRowClick],
  );

  // stores the default sort order for currently sortable column,
  // changes every time a new column is sorted
  const [columnInitialSortOrder, setColumnInitialSortOrder] = useState<
    SortOrder.ASC | SortOrder.DESC
  >((sortOrder?.toLowerCase() as SortOrder) || SortOrder.DESC);

  const onSortChange = useCallback(
    (column, value) => {
      const initialSortOrder: SortOrder = column.initialSortOrder || SortOrder.DESC;
      const isSettingInitialColumn = column.isDefaultSortBy;
      // value is null but we target the default column => skip the null value
      if (isSettingInitialColumn && !value) {
        const nextTick =
          Object.values(SortOrder).find((enumSo) => enumSo !== sortValue?.sortOrder) ||
          initialSortOrder;
        setSortValue({ sortBy: column.key, sortOrder: nextTick });
        onSearchParamChange?.([
          { key: 'sf', value: column.key },
          { key: 'so', value: nextTick },
        ]);
      } else if (!value) {
        setSortValue(null);
        onSearchParamChange?.([
          { key: 'sf', value: undefined },
          { key: 'so', value: undefined },
        ]);
      } else {
        const newSortOrder =
          sortValue?.sortBy === column.key || isSettingInitialColumn ? value : initialSortOrder;
        if (sortValue?.sortBy !== column.key) {
          // if we are sorting by a new column, set default sorting order
          setColumnInitialSortOrder(initialSortOrder);
        }
        setSortValue({ sortBy: column.key, sortOrder: newSortOrder });
        onSearchParamChange?.([
          { key: 'sf', value: column.key },
          { key: 'so', value: newSortOrder },
        ]);
      }
    },
    [onSearchParamChange, sortOrder, sortValue?.sortBy],
  );

  const onFilterChange = useCallback(
    (column, value) => {
      onSearchParamChange?.([
        { key: 'qf', value: value ? column.key : undefined },
        { key: 'q', value },
      ]);
      setSearchValue(value ? { searchBy: column.key, searchText: value } : null);
    },
    [onSearchParamChange],
  );

  const rows = data?.pages.flatMap((page) => [...(page?.edges?.rows || [])]) || [];

  if (!rows.length && !(isFetching || isLoading) && !searchValue && emptyStateCanduSlug) {
    return <div id={emptyStateCanduSlug} />;
  }

  return (
    <>
      <TableCanoe<QueryData>
        loadingFailed={isLoadingError || isRefetchError}
        loading={isLoading || isFetching}
        rows={rows}
        onRow={onRowClick ? onRows : undefined}
        onSortChange={onSortChange}
        onFilterChange={onFilterChange}
        columns={columnsWithOptions}
        sortingColumn={(sortValue?.sortBy || sortField) as string}
        sortingOrder={
          (sortValue?.sortOrder || sortOrder?.toLowerCase() || 'desc') as 'asc' | 'desc'
        }
        defaultSortingOrder={columnInitialSortOrder}
        searchingColumn={searchValue?.searchBy as string}
        searchingValue={searchValue?.searchText}
        onRefetch={refetch}
        onDownload={showDownloadInHeader ? onDownload : undefined}
      />
      {hasNextPage && fetchNextPage ? (
        <Inline space="none" justify="center">
          <Button
            appearance="outline"
            loading={isFetchingNextPage}
            mt="normal"
            mb="normal"
            variant="secondary"
            onClick={() => fetchNextPage()}
          >
            Load more
          </Button>
        </Inline>
      ) : null}
    </>
  );
};
